import { useId, useRef, type ChangeEvent, type ClipboardEvent, type KeyboardEvent } from 'react'
import { cn } from '../../utils/cn'

const LENGTH = 6

interface OtpInputProps {
  value: string
  onChange: (value: string) => void
  /** Fired when the sixth digit lands, so the form can submit itself. */
  onComplete?: (value: string) => void
  error?: boolean
  disabled?: boolean
}

/**
 * Six single-character boxes behaving as one field: typing advances, backspace on
 * an empty box steps back, and a pasted code fills the row. The boxes share one
 * `value` string, so the caller never reassembles digits.
 */
export function OtpInput({ value, onChange, onComplete, error, disabled }: OtpInputProps) {
  const groupId = useId()
  const refs = useRef<(HTMLInputElement | null)[]>([])
  const digits = value.padEnd(LENGTH, ' ').slice(0, LENGTH).split('')

  const commit = (next: string, focusIndex: number) => {
    onChange(next)
    refs.current[Math.min(focusIndex, LENGTH - 1)]?.focus()
    if (next.length === LENGTH) onComplete?.(next)
  }

  const handleChange = (index: number) => (event: ChangeEvent<HTMLInputElement>) => {
    const typed = event.target.value.replace(/\D/g, '').slice(-1)
    if (!typed) return
    const chars = value.padEnd(LENGTH, ' ').split('')
    chars[index] = typed
    commit(chars.join('').trimEnd(), index + 1)
  }

  const handleKeyDown = (index: number) => (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Backspace') return
    event.preventDefault()
    const chars = value.padEnd(LENGTH, ' ').split('')
    // An empty box means the customer wants the previous digit gone.
    const target = chars[index].trim() ? index : Math.max(0, index - 1)
    chars[target] = ' '
    commit(chars.join('').trimEnd(), target)
  }

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault()
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, LENGTH)
    if (!pasted) return
    commit(pasted, pasted.length)
  }

  return (
    <div role="group" aria-label="Verification code" className="flex justify-center gap-2 md:gap-4">
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(el) => {
            refs.current[index] = el
          }}
          id={`${groupId}-${index}`}
          value={digit.trim()}
          onChange={handleChange(index)}
          onKeyDown={handleKeyDown(index)}
          onPaste={handlePaste}
          disabled={disabled}
          inputMode="numeric"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          aria-label={`Digit ${index + 1} of ${LENGTH}`}
          className={cn(
            // Stitch draws the code as six underlined slots, not boxes: no fill, no
            // radius, a 2px rule that turns Oroshi Blue on focus.
            'h-12 w-10 rounded-none border-0 border-b-2 bg-transparent px-0 text-center',
            'font-display text-headline-md text-on-surface transition-colors',
            'focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:opacity-50 md:h-14 md:w-12',
            error ? 'border-error focus:border-error' : 'border-outline-variant focus:border-primary',
          )}
        />
      ))}
    </div>
  )
}
